import { acceptOrRejectReturnThunk, getReturnsThunk } from '@redux/features/return.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState } from 'react';
import AcceptRejectReturnModal from './AcceptRejectReturnModal';

const Returns: FC = () => {
    const [page, setPage] = useState<number>(1);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedReturn, setSelectedReturn] = useState<any>(null);
    const [isAccept, setIsAccept] = useState<boolean>(true);

    // -------------------------------------------------------------
    const dispatch = useAppDispatch();
    // -------------------------------------------------------------
    const { returns, loading, acceptRejectLoading } = useAppSelector((state: RootState) => state.returnState);
    // -------------------------------------------------------------
    const changePage = (p: string) => {
        setPage(Number(p));
    };

    // -------------------------------------------------------------
    useEffect(() => {
        dispatch(getReturnsThunk({ pageSize: 10, pageNumber: page - 1 }));
    }, [page, dispatch]);
    // -------------------------------------------------------------

    const handleOpenModal = (returnItem: any, accept: boolean) => {
        setSelectedReturn(returnItem);
        setIsAccept(accept);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedReturn(null);
    };

    const handleConfirm = () => {
        if (selectedReturn) {
            dispatch(
                acceptOrRejectReturnThunk({
                    returnedItemId: selectedReturn.id,
                    accept: isAccept,
                })
            ).then(() => {
                handleCloseModal();
                // Refresh the returns list
                dispatch(getReturnsThunk({ pageSize: 10, pageNumber: page - 1 }));
            });
        }
    };

    const isReturnProcessed = (status: string) => {
        return status === 'ACCEPTED' || status === 'REJECTED';
    };

    return (
        <>
            <AcceptRejectReturnModal
                show={showModal}
                handleClose={handleCloseModal}
                onConfirm={handleConfirm}
                isAccept={isAccept}
                isLoading={acceptRejectLoading}
                returnItem={selectedReturn}
            />
            <BreadCrumb pageTitle={`Returns List`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Returns List</h3>
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Product Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Lot Number</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Quantity</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Reason Status</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    Reason To Others
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Status</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={returns?.content?.length ?? 0} animationKey={`returns-${page}`}>
                                            {returns?.content?.map((returnItem: any) => (
                                                <AnimatedTableRow
                                                    rowKey={returnItem?.id}
                                                    detailRecord={returnItem}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{returnItem?.id || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {returnItem?.saleOrderItem?.product?.itemName || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {returnItem?.saleOrderItem?.productBatch?.lotNumber || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{returnItem?.quantity || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{returnItem?.reasonStatus || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {returnItem?.reasonToOthers || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{returnItem?.status || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        <div className="flex flex-col justify-center items-center gap-2.5">
                                                            <button
                                                                className={`mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                        rounded-md border border-green-600
                                                        bg-white transition-all duration-300 ease-linear ${
                                                            isReturnProcessed(returnItem?.status)
                                                                ? 'opacity-50 cursor-not-allowed'
                                                                : 'hover:scale-105 hover:bg-green-50'
                                                        }`}
                                                                onClick={() => handleOpenModal(returnItem, true)}
                                                                disabled={
                                                                    acceptRejectLoading ||
                                                                    isReturnProcessed(returnItem?.status)
                                                                }
                                                            >
                                                                Accept
                                                            </button>
                                                            <button
                                                                className={`mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                        rounded-md border border-red-600
                                                        bg-white transition-all duration-300 ease-linear ${
                                                            isReturnProcessed(returnItem?.status)
                                                                ? 'opacity-50 cursor-not-allowed'
                                                                : 'hover:scale-105 hover:bg-red-50'
                                                        }`}
                                                                onClick={() => handleOpenModal(returnItem, false)}
                                                                disabled={
                                                                    acceptRejectLoading ||
                                                                    isReturnProcessed(returnItem?.status)
                                                                }
                                                            >
                                                                Reject
                                                            </button>
                                                        </div>
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={returns?.pageable?.pageSize || 10}
                                    total={returns?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={returns?.numberOfElements || returns?.content?.length || 0}
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default Returns;
